import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { cartLines, formatPrice, deliveryFeeFor, FREE_DELIVERY_MIN, LAUNCH_PLAN_ID } from "../data/products.js";
import ProductImage from "../components/ProductImage.jsx";
import { useJoinLaunchPlan } from "../hooks/useJoinLaunchPlan.js";

import Seo from "../components/Seo.jsx";

export default function Cart() {
  const { items, add, remove, setQty, clear } = useCart();
  const { membership } = useAuth();
  const joinLaunchPlan = useJoinLaunchPlan();
  const lines = cartLines(items);
  const total = lines.reduce((a, l) => a + l.lineTotal, 0);
  const deliveryFee = deliveryFeeFor(lines, { memberFreeDelivery: membership?.active });

  const showLaunchNudge = !items[LAUNCH_PLAN_ID] && !membership?.claimed;

  if (!lines.length) {
    return (
      <section className="section">
        <Seo title="Your Bag" path="/cart" noindex />
        <div className="container cart-empty">
          <div className="cart-empty__icon">🛒</div>
          <h2>Your bag is empty</h2>
          <p>Add some farm-fresh produce to get started.</p>
          <Link to="/products" className="btn btn--primary btn--lg">Browse products</Link>
          {showLaunchNudge && (
            <p className="cart__note cart__note--nudge">
              🎁 Missed the ₹9 founding offer — 12 free eggs?{" "}
              <button type="button" className="auth__linklike" onClick={joinLaunchPlan}>
                Add it back
              </button>
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="section cart-page">
      <Seo title="Your Bag" path="/cart" noindex />
      <div className="container">
        <div className="page-head page-head--cart">
          <h1>Your bag</h1>
        </div>
      </div>
      <div className="container cart">
        <div className="cart__lines">
          <div className="cart__lines-head">
            <div className="cart__lines-cols" aria-hidden="true">
              <span>Product</span>
              <span>Price</span>
              <span>Quantity</span>
            </div>
            <button className="cart__clear" onClick={clear}>Clear all</button>
          </div>
          {lines.map((l) => (
            <div className="cart-line" key={l.key}>
              <div className="cart-line__thumb">
                <ProductImage product={l.product} className="pc__img" override={l.variant?.image} />
              </div>
              <div className="cart-line__info">
                <h3>
                  {l.name}
                  {l.sizeLabel && <span className="cart-line__size">{l.sizeLabel}</span>}
                </h3>
                <button type="button" className="cart-line__remove" onClick={() => setQty(l.key, 0)}>
                  Remove
                </button>
              </div>
              <span className="cart-line__price">{formatPrice(l.price)} <small>/ {l.unit}</small></span>
              <div className="cart-line__stepper">
                <button onClick={() => remove(l.key)} aria-label="Remove one">−</button>
                <span>{l.qty}</span>
                <button onClick={() => add(l.key)} aria-label="Add one">+</button>
              </div>
            </div>
          ))}
        </div>

        <aside className="cart__summary">
          <div className="cart__summary-card">
            <h3>Order summary</h3>
            <div className="cart__row"><span>Items</span><span>{lines.length}</span></div>
            <div className="cart__row"><span>Subtotal</span><span>{formatPrice(total)}</span></div>
            <div className="cart__row">
              <span>Delivery</span>
              {deliveryFee > 0
                ? <span>{formatPrice(deliveryFee)}</span>
                : <span className="cart__free">FREE</span>}
            </div>
            {deliveryFee > 0 && (
              <p className="cart__note cart__note--nudge">
                🚚 Add {formatPrice(FREE_DELIVERY_MIN - total)} more for free delivery.
              </p>
            )}
            {deliveryFee === 0 && membership?.active ? (
              <p className="cart__note cart__note--member">
                🎉 Founding member — free delivery active until{" "}
                {membership.expiresAt.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}.
              </p>
            ) : null}
            <div className="cart__row cart__row--total"><span>Total</span><span>{formatPrice(total + deliveryFee)}</span></div>
            {showLaunchNudge && (
              <p className="cart__note cart__note--nudge">
                🎁 Missed the ₹9 founding offer?{" "}
                <button type="button" className="auth__linklike" onClick={joinLaunchPlan}>
                  Add it back
                </button>
              </p>
            )}
            <Link to="/checkout" className="btn btn--primary btn--block btn--lg cart__checkout-btn">Checkout</Link>
            <p className="cart__note cart__note--center cart__note--guarantee">🔒 Secure online payment</p>
          </div>
        </aside>
      </div>
    </section>
  );
}
